import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useLoaderData, useRevalidator } from "react-router";
import * as api from "../services/api";
import {
  currentLocationFromRequest,
  useCurrentLocation,
} from "../components/LocationSelector";
import * as f from "../utils/formatter";
import {
  assocInIfUnset,
  compose,
  dissocInIf,
  indexBy,
  updateIn,
  updateInIfExists,
} from "shared/src/util.mjs";
import { formData, FormErrorList, useFormAction } from "../form";
import ButtonLoader from "../components/ButtonLoader";
import { useTranslation } from "react-i18next";
import AutocompleteSelect from "../components/AutocompleteSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faTrash } from "@fortawesome/free-solid-svg-icons";
import TimeRangePicker from "../components/mui/TimeRangePicker";

export async function loader({ request }) {
  const location_id = currentLocationFromRequest(request);

  return api.loadReservationSettingsEditData(
    {
      locationServicesFilter: {
        location_id: { eq: location_id },
        service_type: { eq: "other" },
      },
      locationSettingsFilter: {
        location_id: { eq: location_id },
        key: { eq: "reservation_settings" },
      },
    },
    api.contextFromRequest(request),
  );
}

function formatService(service) {
  return `${service.name} (${f.minutesToDuration(service.duration)})`;
}

function ServiceGroupCapacityItemCapacity({
  id,
  capacity,
  formPrefix,
  groupOptions,
  setCapacities,
}) {
  const selectedGroups = new Set(capacity.groups);

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", flexGrow: 1, mt: 1 }}>
        <Box sx={{ flexGrow: 1, mr: 2 }}>
          <AutocompleteSelect
            autoFocus={capacity.groups == null}
            required
            multiple
            label="Skupiny"
            name={`${formPrefix}groups`}
            value={groupOptions.filter((o) => selectedGroups.has(o.id))}
            onChange={(groups) => {
              setCapacities((capacities) => ({
                ...capacities,
                [id]: {
                  ...capacities[id],
                  groups: groups.map((s) => s.id),
                },
              }));
            }}
            options={groupOptions}
          />
        </Box>
        <Box>
          <TextField
            required
            defaultValue={capacity.capacity}
            name={`${formPrefix}capacity`}
            label="Kapacita (%)"
            type="number"
            slotProps={{ htmlInput: { step: "0.1" } }}
          />
        </Box>
      </Box>
      <Box sx={{ alignSelf: "center" }}>
        <IconButton
          onClick={() => {
            setCapacities((capacities) =>
              dissocInIf(capacities, [id], () => true),
            );
          }}
        >
          <FontAwesomeIcon icon={faTrash} />
        </IconButton>
      </Box>
    </Box>
  );
}

const ServiceGroupCapacityItem = React.memo(function ServiceGroupCapacityItem({
  id,
  settings,
  groupOptions,
  setWeeklySettings,
}) {
  return (
    <React.Fragment>
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexGrow: 1,
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <TextField
              autoFocus={settings.from == null}
              required
              defaultValue={settings.from}
              name={`service_group_capacity_weekly.${id}.from`}
              label="Od týdne"
              type="number"
              sx={{ mr: 2 }}
            />
            <TextField
              defaultValue={settings.to}
              name={`service_group_capacity_weekly.${id}.to`}
              label="Do týdne"
              type="number"
            />

            {Object.entries(settings.capacities ?? {}).map(
              ([capacityId, capacity]) => {
                return (
                  <ServiceGroupCapacityItemCapacity
                    key={capacityId}
                    id={capacityId}
                    capacity={capacity}
                    formPrefix={`service_group_capacity_weekly.${id}.capacities.${capacityId}.`}
                    groupOptions={groupOptions}
                    setCapacities={(f) =>
                      setWeeklySettings((settings) =>
                        updateIn(settings, [id, "capacities"], f),
                      )
                    }
                  />
                );
              },
            )}
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setWeeklySettings((settings) =>
                    assocInIfUnset(
                      settings,
                      [id, "capacities", crypto.randomUUID()],
                      {},
                    ),
                  );
                }}
              >
                Nová kapacita
              </Button>
            </Box>
          </Box>
        </Box>
        <Box sx={{ alignSelf: "center", ml: 1 }}>
          <IconButton
            onClick={() => {
              setWeeklySettings((settings) =>
                dissocInIf(settings, [id], () => true),
              );
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </IconButton>
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />
    </React.Fragment>
  );
});

function BufferForm({ reservationSettings }) {
  const [times, setTimes] = React.useState(() => {
    return Object.fromEntries(
      (reservationSettings.buffer?.times ?? []).map((v) => [
        crypto.randomUUID(),
        v,
      ]),
    );
  });

  return (
    <>
      <TextField
        required
        defaultValue={reservationSettings.buffer?.capacity}
        name="buffer.capacity"
        label="Kapacita (%)"
        type="number"
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {Object.entries(times).map(([id, t]) => {
          return (
            <Box
              key={id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <TimeRangePicker
                nameFrom={`buffer.times.${id}.from`}
                nameTo={`buffer.times.${id}.to`}
                defaultValue={[t.from, t.to]}
                slotProps={{
                  textField: {
                    label: "Od - do",
                    required: true,
                  },
                }}
              />
              <Box>
                <IconButton
                  onClick={() => {
                    setTimes((times) => dissocInIf(times, [id], () => true));
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </IconButton>
              </Box>
            </Box>
          );
        })}
        <Button
          variant="outlined"
          onClick={() => {
            setTimes((times) => {
              const id = crypto.randomUUID();

              return { ...times, [id]: { from: "", to: "" } };
            });
          }}
        >
          Přidat čas
        </Button>
      </Box>
    </>
  );
}

function Section({ title, children, onEdit }) {
  return (
    <>
      <Card display="flex" gap="1rem">
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h2" gutterBottom>
              {title}
            </Typography>
            <Button
              onClick={onEdit}
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={faPenToSquare} />}
            >
              Upravit
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
              gap: "5rem",
            }}
          >
            <Box flexGrow="1">{children}</Box>
          </Box>
        </CardContent>
      </Card>
    </>
  );
}

function Info({ label, value }) {
  return (
    <Box display="flex" alignItems="center" gap="0.5rem">
      <strong>{label}:</strong> {value}
    </Box>
  );
}

const formConverter = compose(
  (formData) =>
    updateInIfExists(formData, ["service_group_capacity_weekly"], (v) =>
      Object.values(v),
    ),
  (formData) =>
    updateInIfExists(formData, ["buffer", "times"], (v) => Object.values(v)),
  (formData) =>
    updateInIfExists(formData, ["blocations"], (v) => Object.values(v)),
);

function formConverter2(location_id, reservationSettings, data) {
  return {
    location_id,
    data: {
      reservation_settings: { ...reservationSettings, ...formConverter(data) },
    },
  };
}

function SectionEditDialog({ close, reservationSettings, title, children }) {
  const { t } = useTranslation();
  const location_id = useCurrentLocation();
  const revalidator = useRevalidator();
  const { loading, errors, onSubmit } = useFormAction(
    (e) =>
      api.patchLocationSettings(
        formConverter2(location_id, reservationSettings, formData(e.target)),
      ),
    () => {
      revalidator.revalidate();
      close();
    },
  );

  return (
    <Dialog open onClose={() => close()} maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {children}
            <FormErrorList formErrors={errors} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => close()}>
            {t("form.cancel")}
          </Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading && <ButtonLoader />}
            {t("form.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function GeneralEditDialog({ title, close, reservationSettings }) {
  return (
    <SectionEditDialog
      title={title}
      close={close}
      reservationSettings={reservationSettings}
    >
      <Box display="flex" flexDirection="column" gap={1}>
        <TextField
          required
          defaultValue={reservationSettings.order_from_h}
          name="order_from_h"
          label="Objednání dopředu (h)"
          type="number"
        />
        <TextField
          required
          defaultValue={reservationSettings.available_doctors_plus}
          name="available_doctors_plus"
          label="Maximální počet lékařů = dostupných +"
          type="number"
        />
      </Box>
    </SectionEditDialog>
  );
}

function GeneralInfo({ title, reservationSettings }) {
  const [edit, setEdit] = React.useState(false);

  return (
    <>
      <Section title={title} onEdit={() => setEdit(true)}>
        <Info
          label="Objednání dopředu (h)"
          value={reservationSettings.order_from_h}
        />
        <Info
          label="Maximální počet lékařů = dostupných +"
          value={reservationSettings.available_doctors_plus}
        />
      </Section>
      {edit && (
        <GeneralEditDialog
          title={title}
          close={() => setEdit(false)}
          reservationSettings={reservationSettings}
        />
      )}
    </>
  );
}

function ServiceGroupEditDialog({
  title,
  serviceById,
  serviceGroups: originalServiceGroups,
  close,
  reservationSettings,
}) {
  const [serviceGroups, setServiceGroups] = React.useState(
    originalServiceGroups,
  );
  const serviceOptions = Object.values(serviceById).map((s) => ({
    id: s.id,
    name: formatService(s),
  }));

  return (
    <SectionEditDialog
      title={title}
      close={close}
      reservationSettings={reservationSettings}
    >
      {Object.entries(serviceGroups).map(([id, group]) => {
        const selectedServices = new Set(group.services);

        return (
          <React.Fragment key={id}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  flexGrow: 1,
                }}
              >
                <TextField
                  autoFocus={group.name?.length === 0}
                  required
                  name={`service_groups.${id}.name`}
                  label="Název"
                  defaultValue={group.name}
                />
                <AutocompleteSelect
                  required
                  multiple
                  label="Služby"
                  name={`service_groups.${id}.services`}
                  defaultValue={serviceOptions.filter((o) =>
                    selectedServices.has(o.id),
                  )}
                  options={serviceOptions}
                />
              </Box>
              <Box sx={{ ml: 4 }}>
                <IconButton
                  onClick={() => {
                    setServiceGroups((g) => dissocInIf(g, [id], () => true));
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </IconButton>
              </Box>
            </Box>
            <Divider sx={{ mb: 3, mt: 1 }} />
          </React.Fragment>
        );
      })}
      <Button
        variant="outlined"
        onClick={() => {
          setServiceGroups((settings) => ({
            ...settings,
            [crypto.randomUUID()]: { name: "", services: [] },
          }));
        }}
      >
        Nová skupina
      </Button>
    </SectionEditDialog>
  );
}

function ServiceGroupInfo({
  serviceById,
  serviceGroups,
  reservationSettings,
  title,
}) {
  const [edit, setEdit] = React.useState(false);

  return (
    <>
      <Section title={title} onEdit={() => setEdit(true)}>
        {Object.entries(serviceGroups).map(([id, group]) => {
          return (
            <Box key={id}>
              <List subheader={group.name}>
                {group.services
                  .map((serviceId) => serviceById[serviceId])
                  .filter((s) => s != null)
                  .map((s) => (
                    <ListItem key={s.id}>
                      <ListItemText primary={formatService(s)} />
                    </ListItem>
                  ))}
              </List>
            </Box>
          );
        })}
      </Section>
      {edit && (
        <ServiceGroupEditDialog
          title={title}
          serviceById={serviceById}
          serviceGroups={serviceGroups}
          close={() => setEdit(false)}
          reservationSettings={reservationSettings}
        />
      )}
    </>
  );
}

function ServiceGroupCapacityEditDialog({
  title,
  close,
  reservationSettings,
  weekly,
  groupOptions,
}) {
  const [weeklySettings, setWeeklySettings] = React.useState(() => {
    return Object.fromEntries(weekly.map((s) => [crypto.randomUUID(), s]));
  });

  return (
    <SectionEditDialog
      title={title}
      close={close}
      reservationSettings={reservationSettings}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {Object.entries(weeklySettings).map(([id, settings]) => {
          return (
            <ServiceGroupCapacityItem
              key={id}
              id={id}
              settings={settings}
              groupOptions={groupOptions}
              setWeeklySettings={setWeeklySettings}
            />
          );
        })}

        <Button
          variant="outlined"
          onClick={() => {
            setWeeklySettings((settings) => ({
              ...settings,
              [crypto.randomUUID()]: {
                capacities: { [crypto.randomUUID()]: {} },
              },
            }));
          }}
        >
          Nová týdenní kapacita
        </Button>
      </Box>
    </SectionEditDialog>
  );
}

function ServiceGroupCapacityInfo({
  title,
  groupOptions,
  weeklySettings: weekly,
  reservationSettings,
}) {
  const [edit, setEdit] = React.useState(false);

  return (
    <>
      <Section title={title} onEdit={() => setEdit(true)}>
        {weekly.map((settings, i) => {
          return (
            <Box key={i} sx={{ mb: 4 }}>
              <Typography variant="h3" gutterBottom>
                Týdny: {settings.from} - {settings.to}
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Skupiny</TableCell>
                    <TableCell>Kapacita</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(settings.capacities ?? {}).map(
                    ([capacityId, capacity]) => {
                      const selectedGroups = new Set(capacity.groups);

                      return (
                        <TableRow key={capacityId}>
                          <TableCell sx={{ width: "70%" }}>
                            {groupOptions
                              .filter((o) => selectedGroups.has(o.id))
                              .map((o) => o.name)
                              .join(", ")}
                          </TableCell>
                          <TableCell sx={{ width: "30%" }}>
                            {capacity.capacity}%
                          </TableCell>
                        </TableRow>
                      );
                    },
                  )}
                </TableBody>
              </Table>
            </Box>
          );
        })}
      </Section>
      {edit && (
        <ServiceGroupCapacityEditDialog
          title={title}
          close={() => setEdit(false)}
          reservationSettings={reservationSettings}
          weekly={weekly}
          groupOptions={groupOptions}
        />
      )}
    </>
  );
}

function WaitingListEditDialog({
  title,
  close,
  reservationSettings,
  serviceById,
}) {
  const serviceOptions = Object.values(serviceById).map((s) => ({
    id: s.id,
    name: formatService(s),
  }));
  const selectedServices = new Set(reservationSettings?.waiting_list?.services);

  return (
    <SectionEditDialog
      title={title}
      close={close}
      reservationSettings={reservationSettings}
    >
      <AutocompleteSelect
        required
        multiple
        label="Služby"
        name={`waiting_list.services`}
        defaultValue={serviceOptions.filter((o) => selectedServices.has(o.id))}
        options={serviceOptions}
      />
      <TextField
        required
        defaultValue={reservationSettings.waiting_list?.weeks}
        name="waiting_list.weeks"
        label="Posledních týdnů"
        type="number"
      />
      <TextField
        required
        defaultValue={reservationSettings.waiting_list?.release_h}
        name="waiting_list.release_h"
        label="Uvolnění kapacity zrušeného termínu (h)"
        type="number"
      />
      <TextField
        required
        defaultValue={reservationSettings.waiting_list?.release_capacity_h}
        name="waiting_list.release_capacity_h"
        label="Uvolnění nevyužité kapacity (h)"
        type="number"
        slotProps={{ htmlInput: { max: 120 } }}
      />
    </SectionEditDialog>
  );
}

function WaitingListInfo({ title, reservationSettings, serviceById }) {
  const [edit, setEdit] = React.useState(false);
  const waiting_list = reservationSettings?.waiting_list ?? {};

  return (
    <>
      <Section title={title} onEdit={() => setEdit(true)}>
        <List subheader="Služby">
          {(waiting_list?.services ?? [])
            .map((serviceId) => serviceById[serviceId])
            .filter((s) => s != null)
            .map((s) => (
              <ListItem key={s.id}>
                <ListItemText primary={formatService(s)} />
              </ListItem>
            ))}
        </List>
        <Info
          label="Posledních týdnů"
          value={reservationSettings.waiting_list?.weeks}
        />
        <Info
          label="Uvolnění kapacity zrušeného termínu (h)"
          value={reservationSettings.waiting_list?.release_h}
        />
        <Info
          label="Uvolnění nevyužité kapacity (h)"
          value={reservationSettings.waiting_list?.release_capacity_h}
        />
      </Section>
      {edit && (
        <WaitingListEditDialog
          title={title}
          close={() => setEdit(false)}
          serviceById={serviceById}
          reservationSettings={reservationSettings}
        />
      )}
    </>
  );
}

function ReleaseCapacityEditDialog({ title, close, reservationSettings }) {
  return (
    <SectionEditDialog
      title={title}
      close={close}
      reservationSettings={reservationSettings}
    >
      <Box>
        <TextField
          required
          defaultValue={reservationSettings.release_days}
          name={`release_days`}
          label="Posledních dnů"
          type="number"
        />
      </Box>
    </SectionEditDialog>
  );
}

function ReleaseCapacityInfo({ title, reservationSettings }) {
  const [edit, setEdit] = React.useState(false);

  return (
    <>
      <Section title={title} onEdit={() => setEdit(true)}>
        <Info label="Posledních dnů" value={reservationSettings.release_days} />
      </Section>
      {edit && (
        <ReleaseCapacityEditDialog
          title={title}
          close={() => setEdit(false)}
          reservationSettings={reservationSettings}
        />
      )}
    </>
  );
}

function DailyBufferEditDialog({ title, close, reservationSettings }) {
  return (
    <SectionEditDialog
      title={title}
      close={close}
      reservationSettings={reservationSettings}
    >
      <BufferForm reservationSettings={reservationSettings} />
    </SectionEditDialog>
  );
}

function DailyBufferInfo({ title, reservationSettings }) {
  const [edit, setEdit] = React.useState(false);

  return (
    <>
      <Section title={title} onEdit={() => setEdit(true)}>
        <Info
          label="Kapacita"
          value={`${reservationSettings.buffer?.capacity}%`}
        />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Od</TableCell>
              <TableCell>Do</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(reservationSettings.buffer?.times ?? []).map((t, i) => {
              return (
                <TableRow key={i}>
                  <TableCell>{t.from}</TableCell>
                  <TableCell>{t.to}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Section>
      {edit && (
        <DailyBufferEditDialog
          title={title}
          close={() => setEdit(false)}
          reservationSettings={reservationSettings}
        />
      )}
    </>
  );
}

function ServiceGroupLimitForm({
  serviceGroupLimits: originalServiceGroupLimits,
  serviceById,
}) {
  const [serviceGroupLimits, setServiceGroupLimits] = React.useState(
    originalServiceGroupLimits,
  );
  const serviceOptions = Object.values(serviceById).map((s) => ({
    id: s.id,
    name: formatService(s),
  }));

  return (
    <>
      {Object.entries(serviceGroupLimits).map(([id, group]) => {
        const selectedServices = new Set(group.services);
        const formPrefix = `service_group_limits.${id}.`;

        return (
          <React.Fragment key={id}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  flexGrow: 1,
                }}
              >
                <TextField
                  autoFocus={group.limit == null}
                  required
                  name={`${formPrefix}limit`}
                  label="Max zároveň"
                  defaultValue={group.limit}
                />
                <AutocompleteSelect
                  required
                  multiple
                  label="Služby"
                  name={`${formPrefix}services`}
                  defaultValue={serviceOptions.filter((o) =>
                    selectedServices.has(o.id),
                  )}
                  options={serviceOptions}
                />
              </Box>
              <Box sx={{ ml: 4 }}>
                <IconButton
                  onClick={() => {
                    setServiceGroupLimits((g) =>
                      dissocInIf(g, [id], () => true),
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </IconButton>
              </Box>
            </Box>
          </React.Fragment>
        );
      })}
      <Button
        variant="outlined"
        onClick={() => {
          setServiceGroupLimits((settings) => ({
            ...settings,
            [crypto.randomUUID()]: { services: [], limit: null },
          }));
        }}
      >
        Nová skupina
      </Button>
    </>
  );
}

function ServiceGroupLimitEditDialog({
  title,
  close,
  reservationSettings,
  serviceById,
  serviceGroupLimits,
}) {
  return (
    <SectionEditDialog
      title={title}
      close={close}
      reservationSettings={reservationSettings}
    >
      <ServiceGroupLimitForm
        serviceGroupLimits={serviceGroupLimits}
        serviceById={serviceById}
      />
    </SectionEditDialog>
  );
}

function ServiceGroupLimitInfo({ title, reservationSettings, serviceById }) {
  const [edit, setEdit] = React.useState(false);
  const serviceGroupLimits = reservationSettings.service_group_limits ?? {};

  return (
    <>
      <Section title={title} onEdit={() => setEdit(true)}>
        {Object.entries(serviceGroupLimits).map(([id, group]) => {
          return (
            <Box key={id}>
              <List subheader={`Max zároveň: ${group.limit}`}>
                {group.services
                  .map((serviceId) => serviceById[serviceId])
                  .filter((s) => s != null)
                  .map((s) => (
                    <ListItem key={s.id}>
                      <ListItemText primary={formatService(s)} />
                    </ListItem>
                  ))}
              </List>
            </Box>
          );
        })}
      </Section>
      {edit && (
        <ServiceGroupLimitEditDialog
          title={title}
          close={() => setEdit(false)}
          reservationSettings={reservationSettings}
          serviceById={serviceById}
          serviceGroupLimits={serviceGroupLimits}
        />
      )}
    </>
  );
}

export default function AlgorithmEdit() {
  const loaderData = useLoaderData();
  const services = loaderData.data.locationServices.data.map((ls) => ({
    id: ls.service_id,
    name: ls.service.name,
    duration: ls.duration,
  }));
  const serviceById = indexBy(services, (s) => s.id);

  const reservationSettings =
    loaderData.data.locationSettings.data?.reservation_settings ?? {};

  const serviceGroups = reservationSettings.service_groups ?? {};
  const groupOptions = Object.entries(serviceGroups).map(([k, v]) => ({
    id: k,
    name: v.name,
  }));

  return (
    <>
      <Typography variant="h1" sx={{ mb: 2 }}>
        Parametry
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <GeneralInfo title="Obecné" reservationSettings={reservationSettings} />
        <ServiceGroupInfo
          title="Skupiny služeb"
          reservationSettings={reservationSettings}
          serviceById={serviceById}
          serviceGroups={serviceGroups}
        />
        <ServiceGroupCapacityInfo
          title="Kapacity služeb"
          reservationSettings={reservationSettings}
          groupOptions={groupOptions}
          weeklySettings={
            reservationSettings.service_group_capacity_weekly ?? []
          }
        />
        <ServiceGroupLimitInfo
          title="Maximální počet skupin v jeden čas"
          reservationSettings={reservationSettings}
          serviceById={serviceById}
        />
        <WaitingListInfo
          title="Waiting list"
          reservationSettings={reservationSettings}
          serviceById={serviceById}
        />
        <ReleaseCapacityInfo
          title="Uvolnění kapacity pro libovolné služby"
          reservationSettings={reservationSettings}
        />
        <DailyBufferInfo
          title="Denní buffer"
          reservationSettings={reservationSettings}
        />
      </Box>
    </>
  );
}
